<template>
  <BaseModal
    :modalTitle="$t('area.employee.create.title')"
    :modalIntro="$t('area.employee.create.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :resetCallback="formResetCallback"
      :onSubmit="onSubmit">
      <div class="mb-6">
        <UiInputTextFieldVee
          name="firstName"
          :label="$t('form.label.first-name')"
          :placeholder="$t('form.placeholder.first-name')"
          :rules="['required']"
          isRequired
          v-model="newUser.firstName"
          @update:model-value="resetValidationMessages" />

        <UiInputTextFieldVee
          name="lastName"
          :label="$t('form.label.last-name')"
          :placeholder="$t('form.placeholder.last-name')"
          :rules="['required']"
          isRequired
          v-model="newUser.lastName"
          @update:model-value="resetValidationMessages" />

        <UiInputTextFieldVee
          name="email"
          :label="$t('form.label.email')"
          :placeholder="$t('form.placeholder.email')"
          :rules="['required', 'email']"
          isRequired
          v-model="newUser.email"
          @update:model-value="resetValidationMessages">
          <template v-if="EMU.isEmulatingFirestore" #trailingIcon>
            <button
              type="button"
              class="pointer-events-auto flex h-5 w-5 cursor-pointer items-center justify-center opacity-50 hover:opacity-100"
              v-tooltip="$t('W: Indsæt dummy-email')"
              @click="insertFakeEmail">
              <IconLucideSparkles class="h-5 w-5" />
            </button>
          </template>
        </UiInputTextFieldVee>

        <UiInputPhoneVee
          :name="'phone'"
          :label="$t('form.label.phone')"
          :rules="['requiredPhone', 'phone']"
          isRequired
          v-model="newUser.phone"
          @update:model-value="resetValidationMessages" />

        <RoleSelection
          v-model:userMainRole="newUser.mainRole"
          @update:user-main-role="resetValidationMessages" />

        <WorkAreaSelection
          v-model:userWorkAreas="newUser.workAreas"
          @update:user-work-areas="resetValidationMessages" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { generateFakeEmail } from '@/globals/typescript/utils/fakeEmail'
import { getErrorMessage } from '@/globals/typescript/utils/errorMessages'
import { EMU } from '@/globals/typescript/utils/host'
import { useToast } from '@/globals/typescript/utils/toast'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useUserStore } from '@/stores/userStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import type { ILabManagementUser } from '@team-milva/lab-models/user'
import { mapState } from 'pinia'
import type { PropType } from 'vue'

type TCreateEmployeeForm = Required<
  Pick<
    ILabManagementUser,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'options'
    | 'mainRole'
    | 'workAreas'
  >
>

function createEmployeeForm(): TCreateEmployeeForm {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: {
      number: 0,
      iso2: 'dk',
    },
    options: {
      mustUpdatePassword: true,
      isFirstTimeLogin: true,
      hasPinCode: false,
    },
    mainRole: 'user',
    workAreas: [],
  }
}

export default {
  name: 'CreateEmployeeModal',
  props: {
    onSuccess: {
      type: Function as PropType<(id: string) => void>,
      required: false,
      default: undefined,
    },
  },
  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),

      newUser: createEmployeeForm(),

      isLoading: false,
      showSaveIcon: false,

      errorText: '',

      EMU,
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser', 'isLabAdminUser']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
  },
  methods: {
    insertFakeEmail() {
      this.newUser.email = generateFakeEmail()
      this.resetValidationMessages()
    },
    async onSubmit() {
      this.isLoading = true
      this.resetValidationMessages()

      try {
        if (!this.currentUser?.id || !this.labAccount?.id) {
          this.errorText = this.$t('error.common.standard')
          return
        }

        const userDataCopy: TCreateEmployeeForm = {
          ...this.newUser,
        }

        // TODO: remove cast after deploying and re-running openapi:gen
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)('post', '/users', {
          body: {
            ...userDataCopy,
            labAccountID: this.labAccount.id,
            invitedByUserID: this.isLabAdminUser
              ? undefined
              : this.currentUser.id,
          },
        })

        if (result.success) {
          this.showSaveIcon = true
          setTimeout(() => {
            this.showSaveIcon = false
          }, this.defaultMessageSaveIconTime)
          useToast().success(this.$t('W: Medarbejder oprettet'))
          const createdID = result.data.id
          this.onSuccess?.(createdID)
          return
        }

        if (result.code === 'already-exists') {
          this.errorText = getErrorMessage('auth/email-already-exists')
        } else {
          useToast().error(this.$t('W: Kunne ikke oprette medarbejder'))
          this.errorText = getErrorMessage(result.code)
        }
      } finally {
        this.isLoading = false
      }
    },
    formResetCallback(): boolean {
      if (this.errorText) {
        return false
      }

      this.newUser = createEmployeeForm()

      // Opened from EmployeesListView this modal gets no onSuccess prop, so it
      // never closes on a successful create — it resets in place for the next
      // employee, exactly like CreateTextListItemModal. That makes it the second
      // surface where the baseline has to be retaken *here*, after the reset:
      // UiBaseFormVee calls this once onSubmit() has resolved, so a snapshot
      // left inside onSubmit() would hold the employee just created and leave
      // the modal permanently dirty (MIL-291).
      this.unsavedGuard.takeSnapshot()

      return true
    },
    resetValidationMessages() {
      this.errorText = ''
    },
  },
  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.newUser }),
      modalId: useModalStore().top?.id,
    })
  },
  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

